'use client';

import { useEffect, useState } from 'react';
import { AisLink as Link } from '@/components/aisearch/ais-link';
import {
  cena,
  prodazhaOtkryta,
  razobratTovary,
  tovaryKonspekt,
  vidLicenzii,
  type TovarLicenzii,
} from '@/lib/licenzii';
import { KONSPEKT_KUPIT } from '@/lib/ais-programmy';

/**
 * Цены лицензий Конспекта на посадочной.
 *
 * Из каталога API, а не текстом страницы: каталог — единственный источник
 * цены (так же устроена страница покупки), и посадочная не может обещать
 * одну сумму, когда к оплате будет другая. Пока каталог не ответил или
 * продажа закрыта, блок цен не рисуется вовсе — остаётся ссылка на покупку.
 *
 * Тексты внутри русские, без словаря: раздел «Программы» есть только на
 * русской витрине.
 */
export function CenyKonspekta({ nachalnye }: { nachalnye: TovarLicenzii[] }) {
  // Сервер уже прочитал каталог при сборке страницы (lib/licenzii-katalog):
  // цены видны сразу и есть в HTML. Браузер перечитывает каталог, чтобы
  // между перепроверками страницы показать текущую цену.
  const [tovary, setTovary] = useState<TovarLicenzii[] | null>(
    nachalnye.length > 0 ? nachalnye : null,
  );

  useEffect(() => {
    let zhiv = true;
    void fetch('/api/licenses/products?locale=ru', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null)
      .then((data: unknown) => {
        const svezhie = tovaryKonspekt(razobratTovary(data));
        if (zhiv && svezhie.length > 0) setTovary(svezhie);
      });
    return () => {
      zhiv = false;
    };
  }, []);

  const vidno = tovary !== null && prodazhaOtkryta(tovary);

  return (
    <div className="flex flex-col items-center gap-6">
      {vidno && (
        <div className="grid w-full max-w-[640px] gap-4 sm:grid-cols-2">
          {tovary.map((t) => (
            <div
              key={t.id}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-bg)] p-6 text-left"
            >
              <p className="mb-2 font-semibold">
                {vidLicenzii(t) === 'srok' ? 'На 3 года обновлений' : 'Навсегда'}
              </p>
              <p className="mb-2 text-3xl font-bold text-[var(--color-accent-400)]">
                {cena(t.priceCents, t.currency, 'ru')}
              </p>
              <p className="text-sm text-[var(--color-text-muted)]">
                {vidLicenzii(t) === 'srok'
                  ? 'Все версии, вышедшие за 3 года. Программа работает и после.'
                  : 'Все версии, в том числе будущие.'}
              </p>
            </div>
          ))}
        </div>
      )}
      <Link
        href={KONSPEKT_KUPIT}
        className="inline-flex items-center rounded-full bg-[var(--color-accent-500)] px-8 py-3.5 font-medium text-white transition-colors hover:bg-[var(--color-accent-400)]"
      >
        Купить лицензию
      </Link>
    </div>
  );
}
